// Enhanced file management and versioning
import AuthManager from './auth.js';

class FileManager {
    constructor(projectId) {
        this.projectId = projectId;
        this.currentFiles = [];
        this.fileWatchers = new Map();
    }

    // Enhanced file loading with metadata
    async loadFiles() {
        try {
            const response = await AuthManager.get(`/api/projects/${this.projectId}/files`);
            if (!response.ok) {
                throw new Error('Failed to load files');
            }

            const files = await response.json();
            this.currentFiles = files;
            return files;
        } catch (error) {
            console.error('Error loading files:', error);
            throw error;
        }
    }

    // Get file with version information
    async getFileWithVersions(filename) {
        try {
            const [fileResponse, versionsResponse] = await Promise.all([
                AuthManager.get(`/api/projects/${this.projectId}/files/${filename}`),
                AuthManager.get(`/api/projects/${this.projectId}/files/${filename}/versions`)
            ]);

            if (!fileResponse.ok || !versionsResponse.ok) {
                throw new Error('Failed to load file data');
            }

            const fileData = await fileResponse.json();
            const versions = await versionsResponse.json();

            return {
                ...fileData,
                versions: versions,
                currentVersion: versions.length > 0 ? Math.max(...versions.map(v => v.version)) : 1
            };
        } catch (error) {
            console.error('Error loading file with versions:', error);
            throw error;
        }
    }

    // Create new file
    async createFile(filename, content = '', createdBy = 'user') {
        try {
            const response = await AuthManager.put(`/api/projects/${this.projectId}/files/${filename}`, {
                content: content,
                createdBy: createdBy
            });

            if (!response.ok) {
                throw new Error('Failed to create file');
            }

            const result = await response.json();
            await this.loadFiles(); // Refresh file list
            return result;
        } catch (error) {
            console.error('Error creating file:', error);
            throw error;
        }
    }

    // Update file content (creates new version if by agent)
    async updateFile(filename, content, createdBy = 'user') {
        try {
            const response = await AuthManager.put(`/api/projects/${this.projectId}/files/${filename}`, {
                content: content,
                createdBy: createdBy
            });

            if (!response.ok) {
                throw new Error('Failed to update file');
            }

            const result = await response.json();
            await this.loadFiles(); // Refresh file list
            
            // Notify watchers
            this.notifyFileWatchers(filename, content, createdBy);
            
            return result;
        } catch (error) {
            console.error('Error updating file:', error);
            throw error;
        }
    }

    // Compare two file versions
    async compareVersions(filename, version1, version2) {
        try {
            const [v1Response, v2Response] = await Promise.all([
                AuthManager.get(`/api/projects/${this.projectId}/files/${filename}/versions/${version1}`),
                AuthManager.get(`/api/projects/${this.projectId}/files/${filename}/versions/${version2}`)
            ]);

            if (!v1Response.ok || !v2Response.ok) {
                throw new Error('Failed to load versions for comparison');
            }

            const v1Data = await v1Response.json();
            const v2Data = await v2Response.json();

            return {
                version1: { ...v1Data, version: version1 },
                version2: { ...v2Data, version: version2 },
                diff: this.generateDiff(v1Data.content, v2Data.content)
            };
        } catch (error) {
            console.error('Error comparing versions:', error);
            throw error;
        }
    }

    // Simple diff generation
    generateDiff(content1, content2) {
        const lines1 = content1.split('\n');
        const lines2 = content2.split('\n');
        const diff = [];

        const maxLines = Math.max(lines1.length, lines2.length);
        
        for (let i = 0; i < maxLines; i++) {
            const line1 = lines1[i] || '';
            const line2 = lines2[i] || '';

            if (line1 === line2) {
                diff.push({ type: 'unchanged', content: line1, lineNumber: i + 1 });
            } else if (line1 && !line2) {
                diff.push({ type: 'removed', content: line1, lineNumber: i + 1 });
            } else if (!line1 && line2) {
                diff.push({ type: 'added', content: line2, lineNumber: i + 1 });
            } else {
                diff.push({ type: 'changed', oldContent: line1, newContent: line2, lineNumber: i + 1 });
            }
        }

        return diff;
    }

    // File watching for real-time updates
    watchFile(filename, callback) {
        if (!this.fileWatchers.has(filename)) {
            this.fileWatchers.set(filename, new Set());
        }
        this.fileWatchers.get(filename).add(callback);
    }

    unwatchFile(filename, callback) {
        if (this.fileWatchers.has(filename)) {
            this.fileWatchers.get(filename).delete(callback);
        }
    }

    notifyFileWatchers(filename, content, createdBy) {
        if (this.fileWatchers.has(filename)) {
            this.fileWatchers.get(filename).forEach(callback => {
                try {
                    callback({ filename, content, createdBy, timestamp: new Date() });
                } catch (error) {
                    console.error('Error in file watcher callback:', error);
                }
            });
        }
    }

    // Bulk operations
    async createMultipleFiles(files) {
        const results = [];
        for (const file of files) {
            try {
                const result = await this.createFile(file.name, file.content, file.createdBy);
                results.push({ success: true, filename: file.name, result });
            } catch (error) {
                results.push({ success: false, filename: file.name, error: error.message });
            }
        }
        return results;
    }

    // File search and filtering
    searchFiles(query) {
        const searchTerm = query.toLowerCase();
        return this.currentFiles.filter(file => 
            file.name.toLowerCase().includes(searchTerm)
        );
    }

    filterFilesByType(extension) {
        return this.currentFiles.filter(file => 
            file.name.endsWith(extension)
        );
    }

    // File statistics
    getFileStats() {
        const stats = {
            totalFiles: this.currentFiles.length,
            totalSize: this.currentFiles.reduce((sum, file) => sum + (file.size || 0), 0),
            fileTypes: {},
            lastModified: null
        };

        this.currentFiles.forEach(file => {
            const ext = file.name.split('.').pop() || 'no-extension';
            stats.fileTypes[ext] = (stats.fileTypes[ext] || 0) + 1;

            if (file.modified && (!stats.lastModified || new Date(file.modified) > new Date(stats.lastModified))) {
                stats.lastModified = file.modified;
            }
        });

        return stats;
    }

    // Export file list
    exportFileList() {
        const exportData = {
            projectId: this.projectId,
            exportDate: new Date().toISOString(),
            files: this.currentFiles.map(file => ({
                name: file.name,
                size: file.size,
                modified: file.modified,
                type: file.name.split('.').pop()
            })),
            stats: this.getFileStats()
        };

        const blob = new Blob([JSON.stringify(exportData, null, 2)], {
            type: 'application/json'
        });

        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `file-list-${this.projectId}-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    // Cleanup
    destroy() {
        this.fileWatchers.clear();
        this.currentFiles = [];
    }
}

// Version comparison utilities
class VersionComparator {
    static renderDiff(diff) {
        return diff.map(line => {
            const lineClass = `diff-line diff-${line.type}`;
            let content = '';

            switch (line.type) {
                case 'unchanged':
                    content = `<span class="line-number">${line.lineNumber}</span><span class="line-content">${this.escapeHtml(line.content)}</span>`;
                    break;
                case 'added':
                    content = `<span class="line-number">+${line.lineNumber}</span><span class="line-content">${this.escapeHtml(line.content)}</span>`;
                    break;
                case 'removed':
                    content = `<span class="line-number">-${line.lineNumber}</span><span class="line-content">${this.escapeHtml(line.content)}</span>`;
                    break;
                case 'changed':
                    content = `
                        <div class="changed-line">
                            <div class="old-line">
                                <span class="line-number">-${line.lineNumber}</span>
                                <span class="line-content">${this.escapeHtml(line.oldContent)}</span>
                            </div>
                            <div class="new-line">
                                <span class="line-number">+${line.lineNumber}</span>
                                <span class="line-content">${this.escapeHtml(line.newContent)}</span>
                            </div>
                        </div>
                    `;
                    break;
            }

            return `<div class="${lineClass}">${content}</div>`;
        }).join('');
    }

    static escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    static getChangesSummary(diff) {
        const summary = {
            added: 0,
            removed: 0,
            changed: 0,
            unchanged: 0
        };

        diff.forEach(line => {
            summary[line.type]++;
        });

        return summary;
    }
}

// Export as ES module
export { FileManager, VersionComparator };